/* eslint brace-style: ["error", "allman"] */

const libFableServiceProviderBase = require('fable-serviceproviderbase');

/**
 * Reads exclude soft-deleted rows unless this exact query parameter is present.
 * It is case-sensitive and value-sensitive: `includedeleted`, `IncludeDeleted`
 * and `includeDeleted=1` are all ignored, silently returning zero rows.
 */
const INCLUDE_DELETED_PARAMETER = 'includeDeleted=true';

/**
 * Records are read back in pages of this size while scanning for soft-deleted
 * GUIDs. Kept modest because the read carries every column.
 */
const SCAN_PAGE_SIZE = 500;

/**
 * A scan that would walk more rows than this bails out and reports it rather
 * than silently spending minutes on a table with a large delete history. The
 * caller can raise it deliberately.
 */
const DEFAULT_SCAN_LIMIT = 20000;

/**
 * Revives soft-deleted records whose GUIDs are about to be upserted.
 *
 * Upsert resolves an existing record by reading it back by GUID, and reads
 * exclude soft-deleted rows. A GUID belonging to a deleted row therefore looks
 * absent, so Upsert takes the CREATE branch -- and the create is then rejected
 * by the GUID uniqueness guard, which does see the deleted row:
 *
 *     Error creating record:Error in DAL create: Record with GUID <guid> already exists!
 *
 * That rejection arrives as an `Error` property inside an HTTP **200** body
 * with no identity column, so it does not surface as a transport error. The
 * integration adapter cannot match it either -- its duplicate-detection looks
 * for `Duplicate entry`, which this message does not contain -- so the record
 * burns the retry budget and is then abandoned. The net effect is a record
 * that silently never lands, on a load that reports success.
 *
 * This service closes the gap by reviving those rows before the push, so the
 * subsequent Upsert finds the original and updates it in place.
 *
 * Reviving requires two things from the API:
 *   - reading soft-deleted rows, via `?includeDeleted=true`; and
 *   - `GET /<Entity>/Undelete/:IDRecord`, which exists in meadow-endpoints 4.x
 *     but is NOT registered in 2.1.1 (`api/headlight`). Update cannot
 *     substitute -- meadow excludes the Deleted column from updatable fields,
 *     so clearing it through Update or Upsert returns 200 and changes nothing.
 *
 * `tools/smoke-test-deleted-record-revival.js` asserts all of the above
 * against a live server and is the acceptance test for the 2.x Undelete work.
 */
class HeadlightDeletedRecordReviver extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable
	 * @param {Record<string, any>} [pOptions]
	 * @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'DeletedRecordReviver';

		/** @type {import('headlight-rest-client')} */
		this._Client = null;
	}

	/**
	 * @return {import('headlight-rest-client')}
	 */
	get client()
	{
		return this._Client;
	}

	set client(pClient)
	{
		this._Client = pClient;
	}

	/**
	 * Build the meadow filter that finds soft-deleted rows for an entity,
	 * optionally scoped to one customer.
	 *
	 * @param {number} [pIDCustomer]
	 * @return {string}
	 */
	buildDeletedFilter(pIDCustomer)
	{
		const tmpClauses = [ 'FBV~Deleted~EQ~1' ];
		if (pIDCustomer > 0)
		{
			tmpClauses.push(`FBV~IDCustomer~EQ~${pIDCustomer}`);
		}
		return tmpClauses.join('~');
	}

	/**
	 * Scan an entity's soft-deleted rows and return the ones whose GUID appears
	 * in the supplied set.
	 *
	 * @param {string} pEntity
	 * @param {Set<string>} pGUIDs - GUIDs about to be upserted.
	 * @param {number} [pIDCustomer]
	 * @return {Promise<{ Matches: Array<{ GUID: string, ID: number }>, Scanned: number, Truncated: boolean }>}
	 */
	async findDeletedMatches(pEntity, pGUIDs, pIDCustomer)
	{
		const tmpGUIDColumn = `GUID${pEntity}`;
		const tmpIDColumn = `ID${pEntity}`;
		const tmpFilter = this.buildDeletedFilter(pIDCustomer);
		const tmpScanLimit = this.options.ScanLimit || DEFAULT_SCAN_LIMIT;

		/** @type {Array<{ GUID: string, ID: number }>} */
		const tmpMatches = [];
		let tmpScanned = 0;
		let tmpTruncated = false;

		for (let tmpOffset = 0; ; tmpOffset += SCAN_PAGE_SIZE)
		{
			if (tmpOffset >= tmpScanLimit)
			{
				tmpTruncated = true;
				break;
			}

			/** @type {Array<Record<string, any>>} */
			const tmpPage = await new Promise((resolve, reject) =>
			{
				this.client.getJSON(`${pEntity}s/FilteredTo/${tmpFilter}/${tmpOffset}/${SCAN_PAGE_SIZE}?${INCLUDE_DELETED_PARAMETER}`,
					(pError, pBody) =>
					{
						if (pError)
						{
							return reject(pError);
						}
						return resolve(Array.isArray(pBody) ? pBody : []);
					});
			});

			if (tmpPage.length < 1)
			{
				break;
			}
			tmpScanned += tmpPage.length;

			for (const tmpRecord of tmpPage)
			{
				const tmpGUID = tmpRecord[tmpGUIDColumn];
				if (tmpGUID && pGUIDs.has(tmpGUID))
				{
					tmpMatches.push({ GUID: tmpGUID, ID: tmpRecord[tmpIDColumn] });
				}
			}

			if (tmpPage.length < SCAN_PAGE_SIZE)
			{
				break;
			}
		}

		return { Matches: tmpMatches, Scanned: tmpScanned, Truncated: tmpTruncated };
	}

	/**
	 * @param {string} pEntity
	 * @param {number} pIDRecord
	 * @return {Promise<boolean>}
	 */
	async undeleteRecord(pEntity, pIDRecord)
	{
		return new Promise((resolve) =>
		{
			this.client.getJSON(`${pEntity}/Undelete/${pIDRecord}`,
				(pError, pBody) =>
				{
					if (pError)
					{
						this.log.warn(`Could not undelete [${pEntity}] ID [${pIDRecord}]: ${pError.message || pError}`);
						return resolve(false);
					}
					const tmpCount = (pBody && typeof pBody.Count === 'number') ? pBody.Count : 0;
					if (tmpCount < 1)
					{
						this.log.warn(`Undelete of [${pEntity}] ID [${pIDRecord}] affected no rows.`);
						return resolve(false);
					}
					return resolve(true);
				});
		});
	}

	/**
	 * Revive records from an explicit ledger of `{ EntityName, RecordGUID,
	 * IDRecord }` captured when they were deleted.
	 *
	 * Preferred over scanning when the caller already knows the IDs: it costs
	 * one Undelete call per record instead of paging the entity's whole
	 * delete history.
	 *
	 * @param {Array<{ EntityName: string, RecordGUID: string, IDRecord: number }>} pLedger
	 * @param {Record<string, any>} pComprehension - Only ledger entries whose GUID reappears here are revived.
	 * @return {Promise<{ Considered: number, Revived: number, Failed: number, Skipped: number }>}
	 */
	async reviveFromLedger(pLedger, pComprehension)
	{
		const tmpResult = { Considered: 0, Revived: 0, Failed: 0, Skipped: 0 };
		if (!Array.isArray(pLedger) || pLedger.length < 1)
		{
			return tmpResult;
		}

		/** @type {Map<string, Set<string>>} */
		const tmpIncomingGUIDs = new Map();
		for (const tmpEntity of Object.keys(pComprehension || {}))
		{
			const tmpGUIDColumn = `GUID${tmpEntity}`;
			const tmpRecords = Array.isArray(pComprehension[tmpEntity]) ? pComprehension[tmpEntity] : Object.values(pComprehension[tmpEntity] || {});
			tmpIncomingGUIDs.set(tmpEntity, new Set(tmpRecords.map((pRecord) => String(pRecord[tmpGUIDColumn] || pRecord[`_${tmpGUIDColumn}`] || '')).filter(Boolean)));
		}

		for (const tmpEntry of pLedger)
		{
			const tmpEntityGUIDs = tmpIncomingGUIDs.get(tmpEntry.EntityName);
			if (!tmpEntityGUIDs || !tmpEntityGUIDs.has(String(tmpEntry.RecordGUID)))
			{
				tmpResult.Skipped++;
				continue;
			}
			if (!(Number(tmpEntry.IDRecord) > 0))
			{
				tmpResult.Skipped++;
				continue;
			}

			tmpResult.Considered++;
			const tmpUndeleted = await this.undeleteRecord(tmpEntry.EntityName, Number(tmpEntry.IDRecord));
			if (tmpUndeleted)
			{
				tmpResult.Revived++;
			}
			else
			{
				tmpResult.Failed++;
			}
		}

		if (tmpResult.Revived > 0)
		{
			this.log.info(`Revived ${tmpResult.Revived} previously deleted record(s) from the deletion ledger.`);
		}
		return tmpResult;
	}

	/**
	 * Revive every soft-deleted record in one entity's slice of a comprehension.
	 *
	 * @param {string} pEntity
	 * @param {Record<string, Record<string, any>> | Array<Record<string, any>>} pRecords
	 * @return {Promise<{ Entity: string, Candidates: number, Revived: number, Failed: number, Scanned: number, Truncated: boolean }>}
	 */
	async reviveEntity(pEntity, pRecords)
	{
		const tmpGUIDColumn = `GUID${pEntity}`;
		/** @type {Set<string>} */
		const tmpGUIDs = new Set();
		let tmpIDCustomer = 0;

		const tmpRecordList = Array.isArray(pRecords) ? pRecords : Object.values(pRecords || {});
		for (const tmpRecord of tmpRecordList)
		{
			const tmpGUID = tmpRecord[tmpGUIDColumn] || tmpRecord[`_${tmpGUIDColumn}`];
			if (tmpGUID)
			{
				tmpGUIDs.add(String(tmpGUID));
			}
			if (!tmpIDCustomer && tmpRecord.IDCustomer > 0)
			{
				tmpIDCustomer = Number(tmpRecord.IDCustomer);
			}
		}

		const tmpResult = { Entity: pEntity, Candidates: tmpGUIDs.size, Revived: 0, Failed: 0, Scanned: 0, Truncated: false };
		if (tmpGUIDs.size < 1)
		{
			return tmpResult;
		}

		let tmpFound;
		try
		{
			tmpFound = await this.findDeletedMatches(pEntity, tmpGUIDs, tmpIDCustomer);
		}
		catch (pError)
		{
			// A table without a Deleted column rejects the filter. That is not a
			// failure of the load -- it just cannot hold a soft-deleted row.
			this.log.info(`Skipping delete-revival scan for [${pEntity}]: ${pError.message || pError}`);
			return tmpResult;
		}

		tmpResult.Scanned = tmpFound.Scanned;
		tmpResult.Truncated = tmpFound.Truncated;

		if (tmpFound.Truncated)
		{
			this.log.warn(`Delete-revival scan for [${pEntity}] stopped at the scan limit after ${tmpFound.Scanned} rows; soft-deleted records beyond it were not checked and may upsert as duplicates.`);
		}
		if (tmpFound.Matches.length < 1)
		{
			return tmpResult;
		}

		this.log.info(`Reviving ${tmpFound.Matches.length} soft-deleted [${pEntity}] record(s) before upsert.`);

		for (const tmpMatch of tmpFound.Matches)
		{
			const tmpUndeleted = await this.undeleteRecord(pEntity, tmpMatch.ID);
			if (tmpUndeleted)
			{
				tmpResult.Revived++;
			}
			else
			{
				tmpResult.Failed++;
			}
		}

		return tmpResult;
	}

	/**
	 * Revive soft-deleted records across a whole comprehension.
	 *
	 * @param {Record<string, any>} pComprehension
	 * @param {Array<string>} [pEntitySequence] - Restrict/order the entities scanned.
	 * @return {Promise<{ Entities: Array<Object>, TotalRevived: number, TotalFailed: number }>}
	 */
	async reviveComprehension(pComprehension, pEntitySequence)
	{
		const tmpEntities = pEntitySequence || Object.keys(pComprehension || {});
		const tmpResults = [];
		let tmpTotalRevived = 0;
		let tmpTotalFailed = 0;

		for (const tmpEntity of tmpEntities)
		{
			if (!pComprehension[tmpEntity])
			{
				continue;
			}
			const tmpResult = await this.reviveEntity(tmpEntity, pComprehension[tmpEntity]);
			tmpResults.push(tmpResult);
			tmpTotalRevived += tmpResult.Revived;
			tmpTotalFailed += tmpResult.Failed;
		}

		return { Entities: tmpResults, TotalRevived: tmpTotalRevived, TotalFailed: tmpTotalFailed };
	}
}

module.exports = HeadlightDeletedRecordReviver;
module.exports.default_configuration = { ScanLimit: DEFAULT_SCAN_LIMIT };
module.exports.SCAN_PAGE_SIZE = SCAN_PAGE_SIZE;
module.exports.DEFAULT_SCAN_LIMIT = DEFAULT_SCAN_LIMIT;
module.exports.INCLUDE_DELETED_PARAMETER = INCLUDE_DELETED_PARAMETER;
